import { ActionIcon, Popover, TextInput, type TextInputProps } from '@mantine/core'
import { DatePicker } from '@mantine/dates'
import { useDisclosure, useMergedRef } from '@mantine/hooks'
import { IconCalendar } from '@tabler/icons-react'
import { pickerOpeningDate } from '@/utils/dateInput'
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'

let closeOpenCalendar: (() => void) | null = null

const pad = (n: number) => String(n).padStart(2, '0')

type Digits = string[]

const emptyDigits = (): Digits => Array<string>(8).fill('')

function valueToYMD(value?: string | Date | null): string {
  if (!value) return ''
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return ''
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
  }
  return value.slice(0, 10)
}

function ymdToDigits(ymd: string): Digits {
  if (ymd.length < 10) return emptyDigits()
  const [y, m, d] = ymd.split('-')
  return [m[0], m[1], d[0], d[1], y[0], y[1], y[2], y[3]]
}

function render(digits: Digits): { text: string; slotPos: number[] } {
  let last = -1
  for (let i = 0; i < 8; i++) if (digits[i] !== '') last = i

  const slotPos = Array<number>(9).fill(0)
  let text = ''
  for (let i = 0; i < 8; i++) {
    slotPos[i] = text.length
    if (i <= last) {
      text += digits[i] === '' ? ' ' : digits[i]
      if ((i === 1 || i === 3) && i < last) text += '/'
    }
  }
  slotPos[8] = text.length
  return { text, slotPos }
}

function digitsToYMD(digits: Digits): string | null {
  if (digits.some((d) => d === '')) return null
  const mm = digits.slice(0, 2).join('')
  const dd = digits.slice(2, 4).join('')
  const yyyy = digits.slice(4, 8).join('')
  const month = Number(mm)
  const day = Number(dd)
  const year = Number(yyyy)
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }
  return `${yyyy}-${mm}-${dd}`
}

function ymdToUS(ymd: string): string {
  const [y, m, d] = ymd.split('-')
  return `${m}/${d}/${y}`
}

function posToSlot(slotPos: number[], pos: number): number {
  let best = 0
  let bestDist = Infinity
  for (let s = 0; s <= 8; s++) {
    const dist = Math.abs(slotPos[s] - pos)
    if (dist < bestDist) {
      bestDist = dist
      best = s
    }
  }
  return best
}

export type USDateInputProps = Omit<TextInputProps, 'value' | 'onChange' | 'defaultValue'> & {
  value?: string | Date | null
  onChange?: (value: string | null) => void
  minDate?: string | Date
  maxDate?: string | Date
  defaultDate?: string | Date
  onValidityChange?: (isValid: boolean) => void
}

export const USDateInput = forwardRef<HTMLInputElement, USDateInputProps>(function USDateInput(
  {
    value,
    onChange,
    minDate,
    maxDate,
    defaultDate,
    onValidityChange,
    onFocus,
    onBlur,
    placeholder,
    ...props
  },
  ref,
) {
  const [digits, setDigits] = useState<Digits>(() => ymdToDigits(valueToYMD(value)))
  const [focused, setFocused] = useState(false)
  const [caret, setCaret] = useState(0)
  const [opened, { close, toggle }] = useDisclosure(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const mergedRef = useMergedRef(ref, inputRef)

  const incomingYMD = valueToYMD(value)
  useEffect(() => {
    if (incomingYMD !== (digitsToYMD(digits) ?? '')) {
      setDigits(ymdToDigits(incomingYMD))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingYMD])

  const minYMD = minDate ? valueToYMD(minDate) : ''
  const maxYMD = maxDate ? valueToYMD(maxDate) : ''

  const clampValid = (ds: Digits): string | null => {
    const ymd = digitsToYMD(ds)
    if (!ymd) return null
    if (minYMD && ymd < minYMD) return null
    if (maxYMD && ymd > maxYMD) return null
    return ymd
  }

  const { text: value10, slotPos } = render(digits)

  useLayoutEffect(() => {
    if (focused && inputRef.current) {
      const pos = slotPos[Math.min(caret, 8)]
      inputRef.current.setSelectionRange(pos, pos)
    }
  })

  const commit = (next: Digits, nextCaret: number) => {
    setDigits(next)
    setCaret(nextCaret)
    onChange?.(clampValid(next))
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return
    const { key } = event
    if (/^\d$/.test(key)) {
      event.preventDefault()
      let slot = caret
      while (slot < 8 && digits[slot] !== '') slot++
      if (slot < 8) {
        const next = [...digits]
        next[slot] = key
        commit(next, slot + 1)
      }
    } else if (key === 'Backspace') {
      event.preventDefault()
      if (caret > 0) {
        const next = [...digits]
        next[caret - 1] = ''
        commit(next, caret - 1)
      }
    } else if (key === 'Delete') {
      event.preventDefault()
      if (caret < 8) {
        const next = [...digits]
        next[caret] = ''
        commit(next, caret)
      }
    } else if (key === 'ArrowLeft') {
      event.preventDefault()
      setCaret((c) => Math.max(0, c - 1))
    } else if (key === 'ArrowRight') {
      event.preventDefault()
      setCaret((c) => Math.min(8, c + 1))
    } else if (key === 'Home') {
      event.preventDefault()
      setCaret(0)
    } else if (key === 'End') {
      event.preventDefault()
      setCaret(8)
    } else if (key.length === 1) {
      event.preventDefault()
    }
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.currentTarget.value.replace(/\D/g, '').slice(0, 8)
    const next = emptyDigits()
    for (let i = 0; i < raw.length; i++) next[i] = raw[i]
    commit(next, Math.min(8, raw.length))
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const raw = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 8)
    const next = emptyDigits()
    for (let i = 0; i < raw.length; i++) next[i] = raw[i]
    commit(next, Math.min(8, raw.length))
  }

  const syncCaretFromDom = () => {
    const pos = inputRef.current?.selectionStart ?? 0
    setCaret(posToSlot(slotPos, pos))
  }

  const ymd = digitsToYMD(digits)

  const isEmpty = digits.every((d) => d === '')
  const isComplete = digits.every((d) => d !== '')
  const isValid = isEmpty || clampValid(digits) !== null

  let internalError: string | undefined
  if (isComplete && clampValid(digits) === null) {
    if (!ymd) {
      internalError = 'Please enter a valid date'
    } else if (minYMD && ymd < minYMD) {
      internalError = `Date must be on or after ${ymdToUS(minYMD)}`
    } else if (maxYMD && ymd > maxYMD) {
      internalError = `Date must be on or before ${ymdToUS(maxYMD)}`
    }
  }
  const error = props.error ?? internalError

  useEffect(() => {
    onValidityChange?.(isValid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid])

  return (
    <Popover
      opened={opened && !props.disabled}
      onClose={() => {
        if (closeOpenCalendar === close) closeOpenCalendar = null
        close()
      }}
      position="bottom-start"
      trapFocus={false}
      withinPortal
    >
      <Popover.Target>
        <TextInput
          {...props}
          ref={mergedRef}
          error={error}
          value={value10}
          placeholder={placeholder ?? 'MM/DD/YYYY'}
          inputMode="numeric"
          spellCheck={false}
          autoComplete="off"
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onPaste={handlePaste}
          onClick={syncCaretFromDom}
          onFocus={(event: FocusEvent<HTMLInputElement>) => {
            setFocused(true)
            const pos = event.currentTarget.selectionStart ?? 0
            setCaret(posToSlot(slotPos, pos))
            onFocus?.(event)
          }}
          onBlur={(event: FocusEvent<HTMLInputElement>) => {
            setFocused(false)
            onBlur?.(event)
          }}
          rightSection={
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={(event: MouseEvent) => {
                event.preventDefault()
                if (opened) {
                  closeOpenCalendar = null
                } else {
                  closeOpenCalendar?.()
                  closeOpenCalendar = close
                }
                toggle()
              }}
              disabled={props.disabled}
              tabIndex={-1}
              aria-label="Open calendar"
            >
              <IconCalendar size={16} />
            </ActionIcon>
          }
        />
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <DatePicker
          value={ymd}
          // PD-1901 — the value's month wins; empty opens on the caller's date, else today (in range).
          defaultDate={
            ymd
              ? new Date(`${ymd}T00:00:00`)
              : (defaultDate ?? pickerOpeningDate(null, minDate, maxDate))
          }
          minDate={minDate}
          maxDate={maxDate}
          onChange={(picked) => {
            const next = picked ? ymdToDigits(picked) : emptyDigits()
            commit(next, picked ? 8 : 0)
            close()
          }}
        />
      </Popover.Dropdown>
    </Popover>
  )
})
